/**
 * Toast Component - Fixed for PromptEditor Compatibility
 * 
 * Manages toast notifications with both instance and static methods
 * 
 * @module components/Toast
 */

import Config from '../core/Config.js';

// Add toast styles if not present (moved outside class)
const toastStyles = `
<style id="toast-component-styles">
.toast-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 10000;
  pointer-events: none;
}

.toast {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  margin-bottom: 0.5rem;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  color: white;
  font-size: 0.875rem;
  max-width: 350px;
  opacity: 0;
  transform: translateX(100%);
  transition: all 0.3s ease;
  pointer-events: all;
  cursor: pointer;
}

.toast-show {
  opacity: 1;
  transform: translateX(0);
}

.toast-success {
  background: #10b981;
}

.toast-error {
  background: #ef4444;
}

.toast-warning {
  background: #f59e0b;
}

.toast-info {
  background: #3b82f6;
}

.toast-icon {
  font-weight: bold;
  font-size: 1rem;
  flex-shrink: 0;
}

.toast-message {
  flex: 1;
  line-height: 1.4;
}

.toast-close {
  background: none;
  border: none;
  color: inherit;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  transition: background 0.2s;
  flex-shrink: 0;
}

.toast-close:hover {
  background: rgba(255, 255, 255, 0.2);
}

.toast:hover {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

@media (max-width: 640px) {
  .toast-container {
    left: 10px;
    right: 10px;
    top: 10px;
  }
  
  .toast {
    max-width: none;
  }
}
</style>
`;

// Inject toast styles if not already present
if (typeof document !== 'undefined' && !document.getElementById('toast-component-styles')) {
  document.head.insertAdjacentHTML('beforeend', toastStyles);
}

export class Toast {
  constructor() {
    this.container = null;
    this.toasts = [];
    this._lastShown = new Map(); // key -> timestamp
    this._dupeWindowMs = 1200;
  }
  
  async initialize() {
    // Create container if it doesn't exist
    if (!this.container) {
      this.container = document.createElement('div');
      this.container.className = 'toast-container';
      document.body.appendChild(this.container);
    }
  }
  
  mount(container) {
    this.container = container;
    this.container.className = 'toast-container';
  }
  
  show(message, type = 'info') {
    // Ensure we have a container
    if (!this.container) {
      this.container = document.createElement('div');
      this.container.className = 'toast-container';
      document.body.appendChild(this.container);
    }
    // Dedupe: skip if identical toast fired too recently
    try {
      const key = `${type}:${String(message)}`;
      const now = Date.now();
      const last = this._lastShown.get(key) || 0;
      if (now - last < this._dupeWindowMs) {
        return null;
      }
      this._lastShown.set(key, now);
    } catch (_) {}

    const id = Date.now() + Math.random(); // More unique ID
    const toast = {
      id,
      message,
      type
    };
    
    this.toasts.push(toast);
    
    // Create toast element
    const toastEl = document.createElement('div');
    toastEl.className = `toast toast-${type}`;
    toastEl.dataset.id = id;
    
    const icon = this.getIcon(type);
    
    toastEl.innerHTML = `
      <span class="toast-icon">${icon}</span>
      <span class="toast-message">${message}</span>
      <button class="toast-close" data-action="close">×</button>
    `;
    
    // Add click to close
    toastEl.addEventListener('click', (e) => {
      if (e.target.dataset.action === 'close' || e.target.classList.contains('toast')) {
        this.remove(id);
      }
    });
    
    this.container.appendChild(toastEl);
    
    // Animate in
    setTimeout(() => {
      toastEl.classList.add('toast-show');
    }, 10);
    
    // Auto remove
    const duration = Config.UI?.TOAST_DURATION || 3000;
    setTimeout(() => {
      this.remove(id);
    }, duration);
    
    return id;
  }
  
  remove(id) {
    const toastEl = this.container?.querySelector(`[data-id="${id}"]`);
    if (toastEl) {
      toastEl.classList.remove('toast-show');
      setTimeout(() => {
        if (toastEl.parentNode) {
          toastEl.remove();
        }
      }, 300);
    }
    
    this.toasts = this.toasts.filter(t => t.id !== id);
  }
  
  clear() {
    this.toasts.forEach(toast => this.remove(toast.id));
  }
  
  getIcon(type) {
    const icons = {
      success: '✓',
      error: '✕',
      warning: '⚠',
      info: 'ℹ'
    };
    return icons[type] || icons.info;
  }
}

// Create singleton instance for global use
const toastInstance = new Toast();

// Static methods for compatibility with PromptEditor
Toast.show = function(message, type = 'info') {
  return toastInstance.show(message, type);
};

Toast.success = function(message) {
  return toastInstance.show(message, 'success');
};

Toast.error = function(message) {
  return toastInstance.show(message, 'error');
};

Toast.warning = function(message) {
  return toastInstance.show(message, 'warning');
};

Toast.info = function(message) {
  return toastInstance.show(message, 'info');
};

Toast.clear = function() {
  toastInstance.clear();
};

// Export both class and instance
export { toastInstance };
export default Toast;

// Make available globally
if (typeof window !== 'undefined') {
  window.Toast = Toast;
  window.toastInstance = toastInstance;
  window.showToast = Toast.show; // Also expose as global function
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      toastInstance.initialize();
    });
  } else {
    toastInstance.initialize();
  }
}
